import { Box, Chip, Divider } from '@mui/material';
import { Business_Profile_Enum, Product, Product_Sub_Family_Name_Enum, User_Role_Enum } from '@kheops/graphql';
import { useCallback, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import useSortedProductFamiliesMap, { ProductFamily } from '../../hooks/useSortedProductFamiliesMap';
import { currentContextAtom } from '../../state';
import ProductSubFamilyFilter from '../../products/common/ProductSubFamilyFilter';
import SearchInput from './SearchInput';
import ClearAllButton from './ClearAllButton';

interface ProductFilterToolbarProps {
  products: Pick<Product, 'sub_family'>[];
  initialQuery?: string;
  initialProductFamilies?: Product_Sub_Family_Name_Enum[];
  onChange: (query: string, productFamilies: Product_Sub_Family_Name_Enum[], isActivated: boolean) => void;
  shouldDisplayClearAllButton?: boolean;
  filteredItemsNumber?: number;
}

export default function ProductFilterToolbar({ products, onChange, initialQuery, initialProductFamilies, shouldDisplayClearAllButton = true, filteredItemsNumber }: ProductFilterToolbarProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'contracts']);
  const { realm, roleName, userAdministrativeAreas, userBrands } = useAtomValue(currentContextAtom);
  const [query, setQuery] = useState(initialQuery || '');
  const preferredProductFamiliesMap = useSortedProductFamiliesMap(initialProductFamilies || []);
  const [productFamilies, setProductFamilies] = useState<ProductFamily[]>(preferredProductFamiliesMap);
  const shouldDisplayMyClientsFilter = realm === Business_Profile_Enum.Supplier
    && [User_Role_Enum.SupplierSalesAdmin, User_Role_Enum.SupplierSalesRep].includes(roleName)
    && (
      (!!userBrands?.length && !userBrands[0]?.has_all_brands)
      || (!!userAdministrativeAreas?.length && !userAdministrativeAreas[0].has_all_areas)
    );
  const [isMyClientsFilterActivated, setIsMyClientsFilterActivated] = useState<boolean>(shouldDisplayMyClientsFilter && !!filteredItemsNumber);

  const productFamiliesOptions = useMemo(() => {
    const options = new Set<Product_Sub_Family_Name_Enum>();

    products.forEach((product) => options.add(product.sub_family as Product_Sub_Family_Name_Enum));

    return [...options.values()];
  }, [products]);
  const productFamiliesMap = useSortedProductFamiliesMap(productFamiliesOptions);

  const handleQueryChange = useCallback((inputQuery: string): void => {
    setQuery(inputQuery);
    onChange(inputQuery, productFamilies.map((productFamily) => productFamily.value), isMyClientsFilterActivated);
  }, [onChange, productFamilies, isMyClientsFilterActivated]);

  const handleProductFamiliesChange = useCallback((newProductFamilies: ProductFamily[]): void => {
    setProductFamilies(newProductFamilies);
    onChange(query, newProductFamilies.map((productFamily) => productFamily.value), isMyClientsFilterActivated);
  }, [onChange, query, isMyClientsFilterActivated]);

  const handleMyClientsFiltersChange = useCallback((currentState: boolean): void => {
    setIsMyClientsFilterActivated(!currentState);
    onChange(query, productFamilies.map((productFamily) => productFamily.value), !currentState);
  }, [onChange, query]);

  const clearAll = (): void => {
    setQuery('');
    setProductFamilies([]);
    onChange('', [], false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: {
          xs: 'column',
          sm: 'row',
        },
        alignItems: {
          xs: 'flex-start',
          sm: 'center',
        },
        gap: 1,
      }}
    >
      <SearchInput
        value={query}
        onChange={handleQueryChange}
        sx={{
          width: {
            xs: '100%',
            lg: 400,
          },
        }}
      />
      {realm === Business_Profile_Enum.Buyer && (
        <>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              display: {
                xs: 'none',
                sm: 'block',
              },
              height: 24,
              alignSelf: 'center',
            }}
          />
          <ProductSubFamilyFilter
            productFamilyMap={productFamiliesMap}
            selectedProductFamilyMap={productFamilies}
            onChange={(values) => handleProductFamiliesChange(values)}
          />
        </>
      )}

      {shouldDisplayMyClientsFilter && (
        <>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              display: {
                xs: 'none',
                sm: 'block',
              },
              height: 24,
              alignSelf: 'center',
            }}
          />
          <Chip
            label={`${t('contracts:my_clients_only')} (${filteredItemsNumber})`}
            className={isMyClientsFilterActivated ? 'Kheops-selected' : ''}
            variant="filter"
            disabled={!filteredItemsNumber}
            onClick={() => handleMyClientsFiltersChange(isMyClientsFilterActivated)}
          />
        </>
      )}
      {
        query.length + productFamilies.length > 0 && shouldDisplayClearAllButton && (
          <ClearAllButton
            onClick={clearAll}
          />
        )
      }
    </Box>
  );
}
